/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router, ActivatedRoute} from '@angular/router';

import {AuthenticationService, LoginResponse} from '../services/authentication.service';
import {PasswordResetService} from "../services/password-reset.service";

import {ForgotUsernamePasswordDialogComponent} from "./login/forgot-username-password-dialog.component";
import {LicensingService} from "../services/licensing.service";
import {PagePermissionService} from "../services/page-permission.service";
import {PagePermissionType} from "../models/page-permission-type";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from './simple-success-error-dialog-box/simple-success-error-dialog-box.component';

declare var $: any;

@Component({
  templateUrl: 'login.component.html'
})
export class LoginComponent implements OnInit {

  //Fogot Username/Password Dialog, referenced so that it can be displayed when the User clicks on the link
  @ViewChild('forgotUsernamePasswordDialog')
  mForgotUsernamePasswordDialog: ForgotUsernamePasswordDialogComponent;

    /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  
  //User Id that the User enters to login
  mUserId: string = null;
  
  //Password the User enters to login
  mPassword: string = null;
  
  //Flag used to indicate that the system is trying to log in (waiting for a response from the Server), used to disable UI components
  mLoading = false;
  
  //Flag/String used for error display/handling.  Shown in a popup
  mError = '';
  mDisplayErrorPopup = false;



  constructor(
    private mRoute: ActivatedRoute,
    private mRouter: Router,
    private mAuthenticationService: AuthenticationService,
    private mPasswordResetService: PasswordResetService,
    private pagePermissionService: PagePermissionService
  ) {}

  
  ngOnInit() {
    console.log("ngOnInit()");
    
    //Whenever the Login Page is displayed... Log out any currently logged in User. 
    this.mAuthenticationService.logout();
    localStorage.removeItem("multifactorUser");
    // Make sure there are no modal backdrops active
    $(".modal-backdrop").remove();

    $("#userIdField").trigger("focus");
  }

  
  async loginClicked(): Promise<void> {
    this.mLoading = true;

    console.log(`Login Attempt... User ID: ${this.mUserId}, Password: ${this.mPassword}`);
    
    try {
      //Attempt to log in using the current information in the UI
      const loggedIn = await this.mAuthenticationService.login(this.mUserId, this.mPassword);
      
      this.mLoading = false;
      if (loggedIn.isSuccess) {
        if (loggedIn.isTwoFactorEnabled) {
          this.mRouter.navigateByUrl('/login/mfa');
        } else {
          //User successfully logged into the system, redirect to the home/return page depending on the URL
          this.mRouter.navigateByUrl(this.mRoute.snapshot.queryParams['returnUrl'] || '/home');
        }

        /*
        // User successfully logged into the system - check to see if there is a valid license
        this.licensingService.existsValidLicense().then(
          response => {
            console.log("licensingService.existsValidLicense() == " + response);

            if (response == true) {
              //User successfully logged into the system, redirect to the home/return page depending on the URL
              this.mRouter.navigateByUrl(this.mRoute.snapshot.queryParams['returnUrl'] || '/home');
            } else {
              this.navigateToLicensingPage();
            }

          }, onerror => {
            console.log("Problem grabbing existing licenses");
            this.mLoading = false;
            this.navigateToLicensingPage();
          }
        );
        */
      } else {
        //Login failed... Show an error
        console.log("Failed to login... Error? " + loggedIn.error);
        this.showError(loggedIn.error == null? "Invalid User Name or Password" : loggedIn.error);
      }
    } catch (error) {
      console.log("Problem logging in... Error: " + error);
      this.mLoading = false;
      this.showError(error);
    }
  }

  navigateToLicensingPage(): void {
    // Redirect to licensing page if the user has access to this page, or licensing invalid page
    if (this.pagePermissionService.canRead(PagePermissionType.LicensingPage)) {
      this.mRouter.navigateByUrl(this.mRoute.snapshot.queryParams['returnUrl'] || '/licensing');
    } else {
      this.mRouter.navigateByUrl(this.mRoute.snapshot.queryParams['returnUrl'] || '/invalidLicense');
    }
  }

  showError(message: string): void {
    this.mPassword = "";
    this.mError = message;
    this.mDisplayErrorPopup = true;
    setTimeout(() => { 
      this.mDisplayErrorPopup = false; 
    }, 5000);
    $("#passwordField").trigger("focus");
  }
  
  forgotUsernamePasswordClicked(): void {
    this.mForgotUsernamePasswordDialog.show();
  }
  
  sendPasswordResetEmail(emailAddress: string): void {
    if (emailAddress != null && emailAddress.length > 0) {
      this.mPasswordResetService
        .sendPasswordResetEmail(emailAddress)
        .then(
          success => {
            this.showError("Password reset email sent.")
          },
          error => this.handleError(error)
        );
    }
  }
  
  async handleError(error: any): Promise<void> {
    console.error("handleError() - Error: " + error);
    if (error?.error != null) {
      // Show the error in the SimpleSuccessErrorDialogComponent
          this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }

    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }
  
}
